export const managementRangeOptions = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'all', label: 'All Time' },
  { value: 'custom', label: 'Custom Range' }
];

export function toYmd(date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function managementRangeBounds(range, customStart = '', customEnd = '') {
  if (range === 'custom') {
    return { startDate: customStart, endDate: customEnd };
  }
  
  const current = new Date();
  current.setHours(12, 0, 0, 0);

  switch (range) {
    case 'today':
      return { startDate: toYmd(current), endDate: toYmd(current) };
    case 'yesterday': {
      const yesterday = new Date(current);
      yesterday.setDate(current.getDate() - 1);
      return { startDate: toYmd(yesterday), endDate: toYmd(yesterday) };
    }
    case 'week': {
      const day = current.getDay();
      const offset = day === 0 ? 6 : day - 1;
      const start = new Date(current);
      start.setDate(current.getDate() - offset);
      return { startDate: toYmd(start), endDate: toYmd(current) };
    }
    case 'last_week': {
      const day = current.getDay();
      const offset = day === 0 ? 6 : day - 1;
      const end = new Date(current);
      end.setDate(current.getDate() - offset - 1);
      const start = new Date(end);
      start.setDate(end.getDate() - 6);
      return { startDate: toYmd(start), endDate: toYmd(end) };
    }
    case 'month': {
      const start = new Date(current.getFullYear(), current.getMonth(), 1, 12);
      const end = new Date(current.getFullYear(), current.getMonth() + 1, 0, 12);
      return { startDate: toYmd(start), endDate: toYmd(end) };
    }
    case 'last_month': {
      const start = new Date(current.getFullYear(), current.getMonth() - 1, 1, 12);
      const end = new Date(current.getFullYear(), current.getMonth(), 0, 12);
      return { startDate: toYmd(start), endDate: toYmd(end) };
    }
    case 'all':
    default:
      return { startDate: '', endDate: '' };
  }
}

export function formatManagementCurrency(value) {
  return `Rs${Number(value || 0).toFixed(2)}`;
}

export function formatManagementDate(value) {
  if (!value) return '-';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  const day = `${date.getDate()}`.padStart(2, '0');
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

export function managementStatusTone(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized.includes('completed') || normalized.includes('approved') || normalized.includes('closed')) return 'success';
  if (normalized.includes('progress') || normalized.includes('scheduled')) return 'info';
  if (normalized.includes('pending')) return 'warning';
  if (normalized.includes('late') || normalized.includes('reject')) return 'danger';
  return 'neutral';
}

export function extractClosingRemark(rawRemarks, status = '') {
  if (!rawRemarks) return '-';
  const str = String(rawRemarks).trim();
  if (!str) return '-';

  const lines = str.split('\n').map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return '-';

  // Strategy 1: Look for user line immediately preceding "[System]: Completed"
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^\[System\]:\s*Completed/i.test(line) && i > 0) {
      const prevLine = lines[i - 1];
      const match = prevLine.match(/^\[.*?\]:\s*(.*)$/);
      if (match && match[1]) {
        const text = match[1].trim();
        if (!text.toLowerCase().includes('updated to in progress')) {
          return text;
        }
      } else if (!prevLine.startsWith('[System') && !prevLine.startsWith('[[')) {
        return prevLine;
      }
    }
  }

  // Strategy 2: Look for actual user remarks from bottom up
  // Filter out system generated entries, TAT tags, and automated resume tags
  const candidates = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith('[System') || line.startsWith('[[TAT')) continue;
    if (line.toLowerCase().includes('created from client portal')) continue;

    const match = line.match(/^\[.*?\]:\s*(.*)$/);
    if (match && match[1]) {
      const text = match[1].trim();
      // Skip automated "Updated to In Progress..." lines
      if (!text.toLowerCase().includes('updated to in progress') && !text.toLowerCase().startsWith('work resumed')) {
        candidates.push({ raw: text, isApproval: /^Approved\s*-|^Approve\s*-/i.test(text) });
      }
    } else {
      if (!line.toLowerCase().startsWith('work resumed') && !line.startsWith('[System')) {
        candidates.push({ raw: line, isApproval: false });
      }
    }
  }

  if (candidates.length > 0) {
    // Prefer non-approval user candidate closest to the end (the user's closing/latest remark)
    const userCandidates = candidates.filter((c) => !c.isApproval);
    if (userCandidates.length > 0) {
      return userCandidates[userCandidates.length - 1].raw;
    }
    return candidates[candidates.length - 1].raw;
  }

  return '-';
}

